/** Featured football slides — GR8-style prematch tier only (no live). */

const FOOTBALL_SPORT_IDS = new Set([1]);

/** Competitions GR8 medium-banner typically surfaces. */
const TIER_LEAGUE = [
  { re: /şampiyonlar\s*ligi|champions\s*league|\bucl\b/i, score: 100 },
  { re: /avrupa\s*ligi|europa\s*league|\buel\b/i, score: 90 },
  { re: /konferans\s*ligi|conference\s*league|uecl/i, score: 85 },
  { re: /premier\s*lig/i, score: 80 },
  { re: /la\s*liga/i, score: 78 },
  { re: /serie\s*a/i, score: 76 },
  { re: /bundesliga/i, score: 74 },
  { re: /ligue\s*1|fransa.*1\.?\s*lig/i, score: 72 },
  { re: /süper\s*lig|super\s*lig/i, score: 70 },
  { re: /top\s*teams\s*friendl|kulüpler\s*arası\s*dostluk|clubs?\.\s*top|club\s*friendl|dostluk/i, score: 88 },
  { re: /qualification|qualifying|kvalifikation|eleme|pre-?eliminat/i, score: 95 },
  { re: /leagues?\s*cup|concacaf|copa\s*(libertadores|sudamericana)|libertadores|conmebol/i, score: 78 },
  { re: /brazil\s*cup|copa\s*do\s*brasil|cup\s*of\s*brazil|coupe\s*du\s*bresil/i, score: 76 },
  { re: /england\s*cup|fa\s*cup|copa\s*del\s*rey|dfb\s*pokal|coppa\s*italia|coupe\s*de\s*france|taça|knockout|play-?off/i, score: 74 },
];

const EXCLUDE_LEAGUE = [
  /üniversit|universit|u-?\d{2}|youth|genç|rezerv|reserve|\bii\b|women|kadın|wkbl|amateur|amatör|region|bölgesel|3\.?\s*lig|4\.?\s*lig|league\s*two|national\s*league|tak[iı]m\s*vs\s*oyuncu|team\s*vs\s*player/i,
];

/** Clubs that elevate a friendly into GR8 “top teams” tier. */
const BIG_CLUB = [
  /bayern|dortmund|leverkusen|leipzig|frankfurt/i,
  /real\s*madrid|barcelona|atletico|atlético|sevilla|sociedad|athletic/i,
  /manchester|liverpool|arsenal|chelsea|tottenham|newcastle|aston\s*villa/i,
  /juventus|inter|milan|napoli|roma|lazio/i,
  /psg|paris\s*saint|lyon|marseille|monaco|lille/i,
  /ajax|psv|benfica|porto|sporting/i,
  /celtic|rangers|galatasaray|fenerbahce|fenerbahçe|besiktas|beşiktaş|trabzon/i,
  /ajax|shakhtar|dynamo\s*kyiv|red\s*bull|salzburg|bod[oø]\s*-?\s*glimt|union\s*saint|sparta\s*prague/i,
  /inter\s*miami|miami|tigres|whitecaps|palmeiras|fortaleza|toluca|sounders|guadalajara|lafc|los\s*angeles/i,
  /chelsea|juventus|roma|mallorca|ferencvaros|agf|aarhus|sturm\s*graz/i,
];

const SPORT_BG = {
  1: '/top-events/football.jpg',
};

function leagueTier(league) {
  const s = String(league || '');
  for (const ex of EXCLUDE_LEAGUE) {
    if (ex.test(s)) return -999;
  }
  for (const row of TIER_LEAGUE) {
    if (row.re.test(s)) return row.score;
  }
  return 0;
}

function isBigClub(name) {
  const s = String(name || '');
  return BIG_CLUB.some((re) => re.test(s));
}

function isFriendlyLeague(league) {
  return /friendl|dostluk/i.test(String(league || ''));
}

function hasUsableOdds(ev) {
  const odds = Array.isArray(ev?.odds) ? ev.odds : [];
  return odds.some((o) => !o?.blocked && Number(o?.value) > 1);
}

function kickoffMs(ev) {
  if (!ev?.start_time) return Number.POSITIVE_INFINITY;
  const t = new Date(ev.start_time).getTime();
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}

/**
 * Real GR8 medium-banner only (Fenerbahçe-style player art).
 * No stadium plates, SportsDB compose, or generic sport background fillers.
 */
function hasGr8Banner(ev) {
  if (!ev?.banner_image) return false;
  const src = String(ev.banner_source || '');
  if (src === 'gr8' || src.includes('gr8')) return true;
  return /open-banner\?src=/.test(String(ev.banner_image))
    && /images-platform\.ubo-cms\.com|gr8/i.test(String(ev.banner_image));
}

/**
 * Pick GR8-class featured football: official CDN banner only + tier leagues / big clubs.
 * Matches without a GR8 image are never shown in the homepage slider.
 * Display-only GR8 carousel stubs (gr8_demo) are allowed when they carry CDN art.
 */
export function pickFeaturedFootballMatches(matches, limit = 3) {
  const now = Date.now();
  const list = (Array.isArray(matches) ? matches : [])
    .filter((ev) => {
      if (!ev || ev.finished) return false;
      if (ev.is_live || ev.type === 'live') return false;
      const startMs = kickoffMs(ev);
      if (!Number.isFinite(startMs) || startMs === Number.POSITIVE_INFINITY || startMs <= now) return false;
      if (!ev.home_team || !ev.away_team) return false;
      if (!hasUsableOdds(ev)) return false;
      // Hard requirement: only Fenerbahçe-style GR8 visual matches
      if (!hasGr8Banner(ev)) return false;

      const isDemo = Boolean(ev.gr8_demo);
      if (isDemo) {
        // Seeded from GR8 medium-banner carousel — show even without Nexus ids.
        if (!FOOTBALL_SPORT_IDS.has(Number(ev.sport_id) || 1)) return false;
        return true;
      }

      if (!Number(ev.id) || !Number(ev.sport_id) || !Number(ev.league_id)) return false;
      if (!FOOTBALL_SPORT_IDS.has(Number(ev.sport_id))) return false;

      const odds = Array.isArray(ev.odds) ? ev.odds : [];
      if (!odds.some((o) => Number(o?.id) > 0 && Number(o?.market_id) > 0)) return false;

      const tier = leagueTier(ev.league);
      if (tier < 0) return false;
      const big = isBigClub(ev.home_team) || isBigClub(ev.away_team);
      if (tier >= 70) return true;
      if (big) return true;
      if (isFriendlyLeague(ev.league) && big) return true;
      return false;
    })
    .map((ev) => {
      const start = kickoffMs(ev);
      const hoursToKick = (start - now) / 3600000;
      const big = isBigClub(ev.home_team) || isBigClub(ev.away_team);
      let score = leagueTier(ev.league);
      if (isFriendlyLeague(ev.league) && big) {
        score = Math.max(score, 88);
      }
      if (big) score += 18;
      score += 25;
      // Bonus already guaranteed for banner via filter; keep +40 shape from live
      score += 40;
      // Prefer GR8 carousel order fixtures (real Nexus hit or display stub)
      if (ev.gr8_demo) score += 55;
      else if (String(ev.banner_source || '').includes('gr8')) score += 30;
      if (hoursToKick >= 0 && hoursToKick <= 48) score += 25 - Math.min(hoursToKick, 24);
      else if (hoursToKick > 48 && hoursToKick <= 120) score += 8;
      else if (hoursToKick < 0) score -= 40;
      else score -= 15;
      return { ev, score, start };
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.start - b.start;
    });

  // Do NOT fall back to non-banner matches after banner ones (previous live bug).
  const seen = new Set();
  const out = [];
  for (const row of list) {
    const id = String(row.ev.id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(row.ev);
    if (out.length >= limit) break;
  }
  return out;
}

export function matchBannerBackground(ev) {
  if (ev?.banner_image) return ev.banner_image;
  // Should not reach the slider without a banner after pickFeatured*; keep fallback for safety.
  return SPORT_BG[Number(ev?.sport_id)] || '/top-events/football.jpg';
}

export function interleaveMatchSlides(promoSlides, matches, { showTime = 8 } = {}) {
  const promos = Array.isArray(promoSlides) ? promoSlides : [];
  // Only interleave match slides that still carry a real banner image
  const feats = (Array.isArray(matches) ? matches : []).filter((m) => hasGr8Banner(m));
  if (!feats.length) {
    return promos.map((item) => ({ kind: 'promo', ...item }));
  }
  if (!promos.length) {
    return feats.map((match) => ({
      kind: 'match',
      match,
      showTime,
      img: matchBannerBackground(match),
    }));
  }

  const out = [];
  let mi = 0;
  for (let i = 0; i < promos.length; i += 1) {
    out.push({ kind: 'promo', ...promos[i] });
    if (mi < feats.length) {
      const match = feats[mi];
      mi += 1;
      out.push({
        kind: 'match',
        match,
        showTime,
        img: matchBannerBackground(match),
      });
    }
  }
  while (mi < feats.length) {
    const match = feats[mi];
    mi += 1;
    out.push({
      kind: 'match',
      match,
      showTime,
      img: matchBannerBackground(match),
    });
  }
  return out;
}

export function sportEventFromMatch(ev, odd = null) {
  const event = {
    type: ev?.type === 'live' || ev?.is_live ? 'live' : 'line',
    sportId: Number(ev?.sport_id) || 0,
    leagueId: Number(ev?.league_id) || 0,
    gameId: Number(ev?.id) || 0,
  };
  if (odd && !odd.blocked) {
    event.marketId = Number(odd.market_id) || 0;
    event.oddId = Number(odd.id) || 0;
    event.oddPoint = odd.point ? String(odd.point) : '';
    event.opId = odd.op_id ?? null;
    event.oddLabel = odd.label || '';
    event.oddRate = odd.value;
  }
  return event;
}

export function formatMatchKickoffLabel(startTime, locale) {
  if (!startTime) return '';
  const date = new Date(startTime);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const time = date.toLocaleTimeString(locale === 'tr' ? 'tr-TR' : 'en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return locale === 'en' ? `TODAY, ${time}` : `BUGÜN, ${time}`;
  }
  const day = date.toLocaleDateString(locale === 'tr' ? 'tr-TR' : 'en-GB', {
    day: 'numeric',
    month: 'short',
  });
  return `${day.toUpperCase()}, ${time}`;
}

export function formatOddValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return n % 1 === 0 ? String(n) : n.toFixed(2);
}
